import React from 'react';
import { formatISO } from 'date-fns/formatISO';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Node } from 'slate';

import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  FavoriteBorder, Share, CommentOutlined,
} from '@tmap/mmm-style-guide/src/Icon';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import useLazySSR from '@tmap/mmm-style-guide/src/ssr/useLazySSR';

import getPreviewText from '@tmap/mmm-core/slate/getPreviewText';
import { HASHTAG_TYPE } from '@tmap/mmm-core/slate/constants';

import useCurrentUser from '../../hooks/useCurrentUser';
import LinkCard from '../linkCard';
import ContributionMedia from './contributionMedia';
import { CardProfile } from './cardProfile';
import CardHeader from './cardHeader';
import hideAuthor from '../../lib/content/hideAuthor';

const StyledLinkCard = styled(LinkCard)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.common.white,
  height: '100%',
}));

const FooterGrid = styled(Grid)(() => ({
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
}));

const iconStyle = {
  fontSize: '16px',
  color: (theme) => theme.palette.grey[500],
};

function ContributionPreviewCard(props) {
  const {
    contribution,
    previewTextMaxLen = 300,
    ...otherProps
  } = props;

  const {
    media = [],
    content,
    location,
  } = contribution;

  const user = useCurrentUser();
  const previewTextLonger = getPreviewText(content, { maxLength: 400 });
  const previewText = getPreviewText(content, { maxLength: previewTextMaxLen });
  const authorLocation = [user.personalInfo?.city, user.personalInfo?.state].filter((l) => l).join(', ');

  const isValidHashtagText = (text) => !/#[0-9]*$/.test(text) && text.length > 3;
  const allNodes = content ? Array.from(Node.elements({ children: content })) : [];
  const hashtags = allNodes
    .filter(([element]) => element.type === HASHTAG_TYPE && isValidHashtagText(element.tag))
    .map(([element]) => element.tag.toLowerCase());

  const createdAt = useLazySSR((ssr) => (
    ssr
      ? 'Today'
      : formatISO(new Date())
  ), []);

  return (
    <StyledLinkCard variant='raised' {...otherProps}>
      <Grid container display='block' rowSpacing={1.5} direction='column'>
        <Grid item>
          <CardHeader
            contribution={{ ...contribution, hashtags }}
          />
        </Grid>
        {media.length > 0 && (
          <Grid item>
            <Box sx={{
              borderRadius: '8px', overflow: 'hidden', lineHeight: 0, textAlign: 'center',
            }}
            >
              <ContributionMedia
                media={media[0]}
                allowNonUniformImageHeights={false}
              />
            </Box>
          </Grid>
        )}
        <Grid item container>
          <Grid item xs>
            {contribution.title && (
              <Typography variant='body2' fontWeight={500} fontSize='14px' sx={{ mb: 1 }}>
                {contribution.title}
              </Typography>
            )}
            <Typography variant='body2' fontSize='14px' sx={{ overflowWrap: 'break-word' }}>
              {previewText}{previewTextLonger.length > previewText.length && previewText.length !== 0 && '...'}
            </Typography>
          </Grid>
        </Grid>
        {!hideAuthor({ authorId: contribution.author.userId }) && (
          <Grid item>
            <CardProfile
              author={{ ...(contribution.author || user), location: authorLocation }}
              contribution={{ createdAt, location }}
              showDate
              showLocationPin
            />
          </Grid>
        )}
      </Grid>
      <LinkCard.Footer>
        <Grid container alignItems='flex-end' justifyContent='space-between' sx={{ height: '100%', paddingTop: 2.5 }}>
          <FooterGrid item>
            <CommentOutlined sx={{ ...iconStyle, mr: 0.5 }} />
            <Typography color='textSecondary' component='span' fontSize='10px'>127</Typography>
          </FooterGrid>
          <FooterGrid item>
            <FavoriteBorder sx={{ ...iconStyle, mr: 0.5 }} />
            <Typography color='textSecondary' component='span' fontSize='10px'>3,593</Typography>
          </FooterGrid>
          <FooterGrid item>
            <Share sx={iconStyle} />
          </FooterGrid>
        </Grid>
      </LinkCard.Footer>
    </StyledLinkCard>
  );
}

export default ContributionPreviewCard;
